import React, { useState, useEffect } from 'react';
import { FiX, FiFolder, FiPlus } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './SaveToCollectionModal.css';

const SaveToCollectionModal = ({ isOpen, onClose, work, onSave }) => {
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchCollections();
      fetchActiveCollection();
    }
  }, [isOpen]);

  const fetchCollections = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/collections'), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch collections');
      const data = await response.json();
      setCollections(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchActiveCollection = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/collections/active'), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch active collection');
      const data = await response.json();
      setActiveCollection(data);
    } catch (err) {
      // No active collection is fine
    }
  };

  const handleSave = async (collectionId) => {
    if (!work) return;

    setLoading(true);
    setError('');

    try {
      const workId = work.id.replace('https://openalex.org/', '');
      
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/works`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          work_id: workId,
          work_data: work
        })
      });

      if (!response.ok) {
        // Try to parse error message, fallback if not JSON
        let errorMessage = 'Failed to save work';
        try {
          const data = await response.json();
          errorMessage = data.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON, use default error
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (onSave) {
        onSave(collectionId);
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndSave = async (e) => {
    e.preventDefault();
    if (!newCollectionName.trim()) return;

    setLoading(true);
    setError('');

    try {
      // Create collection
      const createResponse = await fetch(getApiEndpoint('/api/collections'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          name: newCollectionName,
          description: null
        })
      });

      if (!createResponse.ok) {
        let errorMessage = 'Failed to create collection';
        try {
          const data = await createResponse.json();
          errorMessage = data.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON, use default error
        }
        throw new Error(errorMessage);
      }

      const newCollection = await createResponse.json();
      
      // Save work to new collection
      await handleSave(newCollection.id);
      
      setShowCreateModal(false);
      setNewCollectionName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="save-modal-overlay" onClick={onClose}>
      <div className="save-modal" onClick={(e) => e.stopPropagation()}>
        <button className="save-modal-close" onClick={onClose}>
          <FiX />
        </button>

        <div className="save-modal-header">
          <h2>Save to Collection</h2>
          <p className="work-title">{work?.display_name || work?.title || 'Untitled'}</p>
        </div>

        {error && (
          <div className="save-modal-error">
            {error}
          </div>
        )}

        {showCreateModal ? (
          <div className="create-collection-form">
            <h3>Create New Collection</h3>
            <form onSubmit={handleCreateAndSave}>
              <input
                type="text"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                placeholder="Collection name"
                required
                autoFocus
              />
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Create & Save'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <div className="collections-list-modal">
              {activeCollection && (
                <div
                  className="collection-option active-collection"
                  onClick={() => handleSave(activeCollection.id)}
                >
                  <FiFolder className="collection-icon" />
                  <div className="collection-option-info">
                    <h3>{activeCollection.name}</h3>
                    <p>Active Collection • {activeCollection.item_count} items</p>
                  </div>
                  <button className="save-button">Save</button>
                </div>
              )}

              {collections
                .filter(c => !activeCollection || c.id !== activeCollection.id)
                .map((collection) => (
                  <div
                    key={collection.id}
                    className="collection-option"
                    onClick={() => handleSave(collection.id)}
                  >
                    <FiFolder className="collection-icon" />
                    <div className="collection-option-info">
                      <h3>{collection.name}</h3>
                      <p>{collection.item_count} items</p>
                    </div>
                    <button className="save-button">Save</button>
                  </div>
                ))}
            </div>

            <button
              className="create-new-button"
              onClick={() => setShowCreateModal(true)}
            >
              <FiPlus /> Create New Collection
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SaveToCollectionModal;

